import { ChevronLeft, ChevronRight, ImagePlus, Loader2, Pencil, Upload } from "lucide-react";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * Reddit-style media area: large preview, carousel, subtle controls.
 *
 * @param {{
 *   slides: { src: string }[],
 *   slideIndex: number,
 *   onSlideChange: (index: number) => void,
 *   canAddMore: boolean,
 *   uploading: boolean,
 *   dragActive: boolean,
 *   busy: boolean,
 *   onAdd: () => void,
 *   onEditCurrent: () => void,
 *   canReplaceCurrent: boolean,
 *   onRemoveCurrent: () => void,
 *   onDragEnter: (e: import('react').DragEvent) => void,
 *   onDragLeave: (e: import('react').DragEvent) => void,
 *   onDragOver: (e: import('react').DragEvent) => void,
 *   onDrop: (e: import('react').DragEvent) => void,
 * }} props
 */
export default function ClubFeedComposerMediaPanel({
  slides,
  slideIndex,
  onSlideChange,
  canAddMore,
  uploading,
  dragActive,
  busy,
  onAdd,
  onEditCurrent,
  canReplaceCurrent,
  onRemoveCurrent,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}) {
  const n = slides.length;
  const locked = busy || uploading;
  const safeIndex = n === 0 ? 0 : Math.min(Math.max(0, slideIndex), n - 1);
  const current = slides[safeIndex];
  const showCarousel = n > 1;
  const showEmpty = n === 0;

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-3"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div
        className={cn(
          "relative w-full min-w-0 overflow-hidden rounded-xl border border-white/12 bg-[#0e131c]",
          dragActive && canAddMore && "border-white/22 ring-1 ring-white/10",
          showEmpty ? "min-h-[220px]" : "min-h-[200px] max-h-[min(320px,42vh)]"
        )}
      >
        {showEmpty ? (
          <button
            type="button"
            disabled={locked || !canAddMore}
            onClick={onAdd}
            className="flex h-full min-h-[220px] w-full flex-col items-center justify-center gap-3 px-4 py-10 text-center transition hover:bg-white/[0.02] disabled:opacity-40"
          >
            {uploading ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-white/35" aria-hidden />
                <span className="text-[13px] text-gray-500">Uploading…</span>
              </>
            ) : (
              <>
                <Upload className="h-9 w-9 text-white/20" strokeWidth={1.2} aria-hidden />
                <span className="text-[14px] text-white/65">Drag images here or click to upload</span>
              </>
            )}
          </button>
        ) : (
          <>
            <div className="relative flex h-full min-h-[200px] max-h-[min(320px,42vh)] w-full items-center justify-center bg-[#080c12]">
              {current?.src ? (
                <img
                  src={current.src}
                  alt=""
                  className="max-h-[min(320px,42vh)] w-full object-contain"
                  draggable={false}
                />
              ) : (
                <div className="p-6 text-center text-[13px] text-gray-500">Preview unavailable</div>
              )}

              {uploading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0b101a]/80">
                  <Loader2 className="h-9 w-9 animate-spin text-white/45" aria-hidden />
                </div>
              ) : null}

              {!locked && !showEmpty ? (
                <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-end gap-1.5 p-2">
                  <div className="pointer-events-auto flex gap-1.5">
                    {canAddMore ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAdd();
                        }}
                        className="rounded-lg border border-white/10 bg-black/70 px-2.5 py-1.5 text-[11px] font-medium text-white/80 transition hover:border-white/18 hover:bg-black/80 hover:text-white"
                        title="Add images"
                      >
                        <span className="inline-flex items-center gap-1">
                          <ImagePlus className="h-3.5 w-3.5" strokeWidth={2} />
                          Add
                        </span>
                      </button>
                    ) : null}
                    {canReplaceCurrent ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditCurrent();
                        }}
                        className="rounded-lg border border-white/10 bg-black/70 px-2.5 py-1.5 text-[11px] font-medium text-white/80 transition hover:border-white/18 hover:bg-black/80 hover:text-white"
                        title="Replace this image"
                      >
                        <span className="inline-flex items-center gap-1">
                          <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
                          Edit
                        </span>
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveCurrent();
                      }}
                      className="rounded-lg border border-white/10 bg-black/70 px-2.5 py-1.5 text-[11px] font-medium text-white/65 transition hover:border-white/18 hover:bg-black/80 hover:text-rose-200/90"
                      title="Remove this image"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : null}

              {showCarousel ? (
                <>
                  <button
                    type="button"
                    disabled={locked}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSlideChange(safeIndex <= 0 ? n - 1 : safeIndex - 1);
                    }}
                    className="absolute left-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/50 text-white/80 transition hover:border-white/20 hover:bg-black/65 disabled:opacity-30"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="h-5 w-5" strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    disabled={locked}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSlideChange(safeIndex >= n - 1 ? 0 : safeIndex + 1);
                    }}
                    className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/50 text-white/80 transition hover:border-white/20 hover:bg-black/65 disabled:opacity-30"
                    aria-label="Next image"
                  >
                    <ChevronRight className="h-5 w-5" strokeWidth={2} />
                  </button>
                  <div
                    className="absolute bottom-2.5 left-0 right-0 flex justify-center gap-1.5"
                    role="tablist"
                    aria-label="Image position"
                  >
                    {slides.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        role="tab"
                        aria-selected={i === safeIndex}
                        disabled={locked}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSlideChange(i);
                        }}
                        className={cn(
                          "h-1.5 rounded-full transition-all duration-200",
                          i === safeIndex ? "w-5 bg-white/55" : "w-1.5 bg-white/20 hover:bg-white/35",
                          locked && "opacity-40"
                        )}
                        aria-label={`Image ${i + 1} of ${n}`}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </>
        )}
      </div>

    </div>
  );
}
